import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, CheckCircle, Globe, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const API = "/api";

const RegisterPromoter = () => {
  const [submitted, setSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    description: '',
    years_experience: '',
    event_types: '',
    typical_venues: '',
    website: '',
    instagram: '',
    facebook: '',
    past_events: '',
    notable_artists: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const cleanForm = {
        ...form,
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
      };
      
      const response = await axios.post(`${API}/register/promoter`, cleanForm);
      setRegistrationId(response.data.id);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="promoter-registration-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md space-y-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Registration Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for registering as an event promoter with Twin Tube! We'll review your application and get back to you within 3-5 business days.
          </p>
          <div className="p-4 bg-card rounded-sm border border-border">
            <p className="text-sm text-muted-foreground mb-1">Your Reference ID:</p>
            <code className="text-primary font-mono text-sm">{registrationId}</code>
          </div>
          <p className="text-sm text-muted-foreground">
            Once approved, you'll be able to submit and promote events on our platform!
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
            <Link to="/events">
              <Button>View Events</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="register-promoter-page">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={16} />
        Back to Home
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
          <Megaphone size={32} className="text-primary" />
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Promoter Registration
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Join Twin Tube as a verified event promoter. Once approved, you'll be able to 
          post and promote events across the Twin Cities.
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Company Information</CardTitle>
              <CardDescription>Tell us about your promotion company or brand</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company/Brand Name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => updateForm('company_name', e.target.value)}
                    placeholder="Your company or brand name"
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input
                    type="number"
                    value={form.years_experience}
                    onChange={(e) => updateForm('years_experience', e.target.value)}
                    placeholder="e.g., 5"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>About Your Company *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  placeholder="Tell us about your promotion company, what types of events you specialize in, and your vision..."
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Types</Label>
                  <Input
                    value={form.event_types}
                    onChange={(e) => updateForm('event_types', e.target.value)}
                    placeholder="e.g., Concerts, Festivals, Club Nights"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Typical Venues</Label>
                  <Input
                    value={form.typical_venues}
                    onChange={(e) => updateForm('typical_venues', e.target.value)}
                    placeholder="e.g., First Avenue, Varsity Theater"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Contact Information</CardTitle>
              <CardDescription>How can we reach you?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => updateForm('contact_name', e.target.value)}
                    placeholder="Your name"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="promoter@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="(612) 555-0123"
                />
              </div>
            </CardContent>
          </Card>

          {/* Online Presence */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Globe size={20} /> Online Presence
              </CardTitle>
              <CardDescription>Your website and social media</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => updateForm('website', e.target.value)}
                    placeholder="https://yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={form.instagram}
                    onChange={(e) => updateForm('instagram', e.target.value)}
                    placeholder="@yourcompany"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Facebook</Label>
                  <Input
                    value={form.facebook}
                    onChange={(e) => updateForm('facebook', e.target.value)}
                    placeholder="https://facebook.com/yourcompany"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Portfolio */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Portfolio & Experience</CardTitle>
              <CardDescription>Tell us about your past work</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Past Events</Label>
                <Textarea
                  value={form.past_events}
                  onChange={(e) => updateForm('past_events', e.target.value)}
                  placeholder="Describe some of your past events, attendance numbers, and successes..."
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Notable Artists You've Worked With</Label>
                <Textarea
                  value={form.notable_artists}
                  onChange={(e) => updateForm('notable_artists', e.target.value)}
                  placeholder="List any notable artists or acts you've booked or worked with..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full gap-2"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Registration'}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By submitting, you confirm that all information provided is accurate.
            We typically review applications within 3-5 business days.
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterPromoter;
